import { type ReactNode, useEffect } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ChevronRight, Download, RefreshCw, Send, Sparkles } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Alert } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import { TimeseriesChart } from "@/components/ui/chart"
import {
  bandBadge,
  categoryLabel,
  type DriverFlag,
  fmtDelta,
  fmtIdr,
  fmtInt,
  fmtPct,
  type Narration,
  NARRATION_ACTIVE,
  NarratedProse,
  NarrationStatusBadge,
  narrationSourceLabels,
  OwnBadge,
  type Period,
  type Ref,
  type Snapshot,
  SourceRefs,
} from "./shared"

type Crumb = { label: string; href: string }
type TrendPoint = { label: string; internal_share_pct: number | null; nielsen_share_pct: number | null }

type Props = {
  snapshot: Snapshot
  trend: TrendPoint[]
  selected_period: Period
  available_periods: Period[]
  breadcrumb: Crumb[]
  narration: Narration | null
  narration_enabled: boolean
  can_generate: boolean
  can_publish: boolean
}

const periodLabel = (p: Period) => `${p.year}-${String(p.month).padStart(2, "0")}`

function Crumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted" aria-label="Breadcrumb">
      <Link href="/admin/product-performance" className="hover:text-ink-body">
        Performa Produk
      </Link>
      {crumbs.map((c) => (
        <span key={c.href} className="inline-flex items-center gap-1.5">
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
          <Link href={c.href} className="hover:text-ink-body">
            {c.label}
          </Link>
        </span>
      ))}
    </nav>
  )
}

function MetricCard({
  title,
  value,
  badge,
  delta,
  refs,
}: {
  title: string
  value: ReactNode
  badge?: ReactNode
  delta?: ReactNode
  refs?: Ref[]
}) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-ink-muted">{title}</span>
        {badge}
      </div>
      <div className="mt-2 font-display text-2xl text-ink-display">{value}</div>
      {delta && <div className="mt-0.5 text-xs text-ink-muted">{delta}</div>}
      {refs && <SourceRefs refs={refs} />}
    </div>
  )
}

function DriverChip({ label, flag }: { label: string; flag: DriverFlag }) {
  return (
    <div className="inline-flex flex-col">
      <Badge tone={flag.present ? "accent" : "muted"}>
        {label}: {flag.present ? "ada" : "tidak"}
      </Badge>
      {flag.present && <SourceRefs refs={flag.refs} />}
    </div>
  )
}

export default function Show({
  snapshot,
  trend,
  selected_period,
  available_periods,
  breadcrumb,
  narration,
  narration_enabled,
  can_generate,
  can_publish,
}: Props) {
  const path = "/admin/product-performance"
  const id = snapshot.product_variant.product_variant_id
  const m = snapshot.metrics
  const ev = snapshot.driver_evidence
  const cmp = `vs ${periodLabel(snapshot.comparison)}`
  const name = snapshot.product_variant.canonical_name
  const description = `Performa deterministik untuk ${name} — tren hasil, konteks pendorong, dan perbandingan own-vs-kompetitor untuk ${periodLabel(selected_period)}.`

  const slStock = [...snapshot.stock.period.refs, ...snapshot.service_level.period.refs]
  const gapRefs = snapshot.price.gap.flatMap((g) => g.refs)

  return (
    <>
      <Head title={`${name} — Performa Produk`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${name} — Performa Produk`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell width="wide">
        <Crumbs crumbs={breadcrumb} />

        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1>{name}</h1>
                <OwnBadge isOwn={snapshot.product_variant.is_own} />
              </div>
              <p className="mt-1 text-sm text-ink-muted">
                {[
                  snapshot.product_variant.brand,
                  snapshot.product_variant.manufacturer,
                  categoryLabel(snapshot.product_variant.tracked_category),
                  snapshot.product_variant.pack_size,
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </p>
            </div>
            {available_periods.length > 0 && (
              <div className="min-w-[160px]">
                <label htmlFor="period">Periode</label>
                <Select
                  id="period"
                  className="mt-1"
                  value={periodLabel(selected_period)}
                  onChange={(e) => {
                    const [year, month] = e.target.value.split("-")
                    router.get(`${path}/${id}`, { year, month }, { preserveScroll: true })
                  }}
                >
                  {available_periods.map((p) => (
                    <option key={periodLabel(p)} value={periodLabel(p)}>
                      {periodLabel(p)}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* DQ banner */}
        {snapshot.dq_flags.length > 0 && (
          <div className="mt-6 rounded-lg border border-hairline bg-surface p-4">
            <div className="flex items-center gap-2">
              <Badge tone="signal">Kualitas data</Badge>
              <span className="text-sm text-ink-body">
                Sebagian join level-produk masih tipis (tergantung resolusi HITL #48). Angka di bawah hanya dari baris yang
                ter-resolve.
              </span>
            </div>
            <ul className="mt-2 flex flex-wrap gap-2">
              {snapshot.dq_flags.map((d, i) => (
                <li key={`${d.metric}-${d.flag}-${i}`}>
                  <Badge tone="muted">
                    {d.metric}: {d.flag}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* AI "why" narration (M6) — rendered only when the kill-switch is on. */}
        <NarrationSection
          leafId={id}
          period={selected_period}
          narration={narration}
          enabled={narration_enabled}
          canGenerate={can_generate}
          canPublish={can_publish}
        />

        {/* Driver evidence — the deterministic "why" label (evidence, not causation) */}
        <section className="mt-8">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-sm font-medium text-ink-display">Bukti pendorong</h2>
            <Badge tone={ev.present ? "success" : "muted"}>
              {ev.present ? "Driver teridentifikasi" : "Belum dapat dijelaskan"}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            Pendorong yang hadir saat angka bergerak — bukti deterministik, bukan klaim sebab-akibat.
          </p>
          <div className="mt-3 flex flex-wrap gap-4">
            <DriverChip label="Harga promo" flag={ev.drivers.price_promo} />
            <DriverChip label="Aktivitas terencana" flag={ev.drivers.planned_activity} />
            <DriverChip label="Rencana diskon" flag={ev.drivers.discount_plan} />
          </div>
        </section>

        {/* Headline metrics */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-ink-display">Metrik utama — {periodLabel(selected_period)}</h2>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              title="Share internal"
              value={fmtPct(m.internal_share_pct)}
              badge={bandBadge("share", m.internal_share_trend)}
              delta={`${fmtDelta(m.internal_share_delta_pp)} ${cmp}`}
              refs={snapshot.internal_share.period.refs}
            />
            <MetricCard
              title="Share NIQ (own brand)"
              value={fmtPct(m.nielsen_share_pct)}
              badge={bandBadge("share", m.nielsen_share_trend)}
              delta={`${fmtDelta(m.nielsen_share_delta_pp)} ${cmp}`}
              refs={snapshot.nielsen.period.own_brand_share?.refs ?? snapshot.nielsen.period.refs}
            />
            <MetricCard
              title="DOI (hari)"
              value={m.doi_days == null ? "—" : fmtInt(m.doi_days)}
              badge={bandBadge("doi", m.doi_band)}
              refs={slStock}
            />
            <MetricCard
              title="Sell-through"
              value={fmtPct(m.sell_through_pct)}
              badge={bandBadge("sell_through", m.sell_through_band)}
              refs={slStock}
            />
            <MetricCard
              title="Gap harga vs pasar"
              value={m.price_gap_pct == null ? "—" : fmtPct(m.price_gap_pct)}
              badge={bandBadge("price_gap", m.price_gap_band)}
              refs={gapRefs}
            />
            <MetricCard
              title="Diskon rencana"
              value={m.discount_pct == null ? "—" : `${Math.round(m.discount_pct * 100)}%`}
              badge={bandBadge("discount", m.discount_band)}
              refs={snapshot.discount.hero.flatMap((h) => h.refs)}
            />
          </div>
        </section>

        {/* Share trend (6-month rolling) */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-ink-display">Tren market share (6 bulan)</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Share internal dan share NIQ (merek own) — sinyal terpadat saat data level-produk masih tipis.
          </p>
          <div
            className="mt-3 rounded-lg border border-hairline bg-surface p-4"
            role="img"
            aria-label={`Tren market share 6 bulan: share internal ${fmtPct(m.internal_share_pct)} dan share NIQ own ${fmtPct(m.nielsen_share_pct)} pada ${periodLabel(selected_period)}.`}
          >
            <TimeseriesChart
              data={trend as unknown as Array<Record<string, number | string | null>>}
              series={[
                { key: "internal_share_pct", name: "Share internal" },
                { key: "nielsen_share_pct", name: "Share NIQ (own)" },
              ]}
              valueFormatter={(v) => (typeof v === "number" && !Number.isNaN(v) ? `${v.toFixed(1)}%` : "")}
              height={300}
              emptyMessage="Belum ada data share untuk produk ini."
            />
          </div>
        </section>

        {/* Result + driver context, side by side */}
        <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Result */}
          <div>
            <h2 className="text-sm font-medium text-ink-display">Hasil (result)</h2>
            <dl className="mt-3 space-y-3">
              <ResultRow label="Sell-in (receipt / net)" refs={snapshot.sell_in.period.refs}>
                {fmtInt(snapshot.sell_in.period.receipt_qty)} / {fmtInt(snapshot.sell_in.period.net_qty)}
              </ResultRow>
              <ResultRow label="Stok (DC + toko)" refs={snapshot.stock.period.refs}>
                {fmtInt(snapshot.stock.period.total_stock_qty)}
              </ResultRow>
              <ResultRow label="Service level" refs={snapshot.service_level.period.refs}>
                {fmtPct(snapshot.service_level.period.service_level_pct)} ({fmtInt(snapshot.service_level.period.receipt_qty)} /{" "}
                {fmtInt(snapshot.service_level.period.po_qty)})
              </ResultRow>
              <ResultRow label="Sell-out (distributor)" refs={snapshot.sell_out.period.refs} note="Grain channel berbeda — dilaporkan terpisah">
                {fmtInt(snapshot.sell_out.period.qty_total_pcs)}
              </ResultRow>
            </dl>
          </div>

          {/* Drivers */}
          <div>
            <h2 className="text-sm font-medium text-ink-display">Pendorong (driver)</h2>

            <h3 className="mt-3 text-xs font-medium uppercase tracking-wide text-ink-muted">Harga (own)</h3>
            {snapshot.price.own.length === 0 ? (
              <p className="mt-1 text-sm text-ink-muted">Tidak ada harga ter-resolve untuk leaf ini.</p>
            ) : (
              <ul className="mt-1 space-y-2">
                {snapshot.price.own.map((p, i) => (
                  <li key={`${p.channel}-${i}`} className="rounded-md border border-hairline bg-surface p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-ink-body">{p.channel}</span>
                      <span className="text-xs text-ink-muted">{p.snapshot}</span>
                    </div>
                    <div className="mt-1 text-ink-body">
                      {fmtIdr(p.shelf_price_idr)}
                      {p.promo_price_idr != null && <span className="text-ink-muted"> · promo {fmtIdr(p.promo_price_idr)}</span>}
                      {p.promo_period_label && <span className="text-ink-muted"> ({p.promo_period_label})</span>}
                    </div>
                    <SourceRefs refs={p.refs} />
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mt-4 text-xs font-medium uppercase tracking-wide text-ink-muted">Aktivitas merek</h3>
            {snapshot.activity.active.length === 0 ? (
              <p className="mt-1 text-sm text-ink-muted">Tidak ada aktivitas pada {snapshot.activity.window}.</p>
            ) : (
              <ul className="mt-1 space-y-2">
                {snapshot.activity.active.map((a, i) => (
                  <li key={i} className="rounded-md border border-hairline bg-surface p-3 text-sm">
                    <span className="font-medium text-ink-body">{a.activity_type ?? "Aktivitas"}</span>
                    {a.description && <span className="text-ink-muted"> — {a.description}</span>}
                    <SourceRefs refs={a.refs} />
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mt-4 text-xs font-medium uppercase tracking-wide text-ink-muted">
              Diskon (Cosmo · {snapshot.discount.fiscal_year})
            </h3>
            {snapshot.discount.hero.length === 0 && snapshot.discount.plans.length === 0 ? (
              <p className="mt-1 text-sm text-ink-muted">Tidak ada rencana diskon untuk periode ini.</p>
            ) : (
              <ul className="mt-1 space-y-2">
                {snapshot.discount.hero.map((h, i) => (
                  <li key={`hero-${i}`} className="rounded-md border border-hairline bg-surface p-3 text-sm">
                    <span className="font-medium text-ink-body">{h.account_name ?? "—"}</span>
                    <span className="text-ink-muted"> — maks {h.max_disc_pct == null ? "—" : `${Math.round(h.max_disc_pct * 100)}%`}</span>
                    {h.hero_label && <span className="text-ink-muted"> · {h.hero_label}</span>}
                    <SourceRefs refs={h.refs} />
                  </li>
                ))}
                {snapshot.discount.plans.map((pl, i) => (
                  <li key={`plan-${i}`} className="text-sm text-ink-muted">
                    {pl.account_name}: {pl.product_count} produk
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Own vs competitor (NIQ brand shares) */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-ink-display">Own vs kompetitor (share NIQ)</h2>
          {snapshot.nielsen.period.own_brand_share == null && snapshot.nielsen.period.competitors.length === 0 ? (
            <p className="mt-1 text-sm text-ink-muted">Tidak ada data share NIQ untuk merek ini pada periode terpilih.</p>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Merek</th>
                    <th scope="col" className="px-4 py-3 font-medium">Share</th>
                    <th scope="col" className="px-4 py-3 font-medium">Penanda</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.nielsen.period.own_brand_share && (
                    <tr className="border-t border-hairline">
                      <td className="px-4 py-3 font-medium text-ink-body">
                        {snapshot.nielsen.period.own_brand_share.dimension.brand ?? snapshot.product_variant.brand}
                      </td>
                      <td className="px-4 py-3 text-ink-body">{fmtPct(snapshot.nielsen.period.own_brand_share.share_pct)}</td>
                      <td className="px-4 py-3">
                        <Badge tone="accent">Sendiri</Badge>
                      </td>
                    </tr>
                  )}
                  {snapshot.nielsen.period.competitors.map((c, i) => (
                    <tr key={i} className="border-t border-hairline">
                      <td className="px-4 py-3 text-ink-body">{c.dimension.brand}</td>
                      <td className="px-4 py-3 text-ink-body">{fmtPct(c.share_pct)}</td>
                      <td className="px-4 py-3">
                        <Badge tone="muted">Kompetitor</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {snapshot.price.gap.length > 0 && (
            <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Channel</th>
                    <th scope="col" className="px-4 py-3 font-medium">Kategori</th>
                    <th scope="col" className="px-4 py-3 font-medium">Harga own</th>
                    <th scope="col" className="px-4 py-3 font-medium">Harga kompetitor</th>
                    <th scope="col" className="px-4 py-3 font-medium">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.price.gap.map((g, i) => (
                    <tr key={i} className="border-t border-hairline">
                      <td className="px-4 py-3 text-ink-body">{g.channel}</td>
                      <td className="px-4 py-3 text-ink-muted">{g.category_name ?? "—"}</td>
                      <td className="px-4 py-3 text-ink-body">{fmtIdr(g.own_price)}</td>
                      <td className="px-4 py-3 text-ink-body">{fmtIdr(g.competitor_price)}</td>
                      <td className="px-4 py-3 text-ink-body">{g.gap_pct == null ? "—" : fmtPct(g.gap_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </AppShell>
    </>
  )
}

function ResultRow({ label, children, refs, note }: { label: string; children: ReactNode; refs: Ref[]; note?: string }) {
  return (
    <div className="rounded-md border border-hairline bg-surface p-3">
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-sm text-ink-muted">{label}</dt>
        <dd className="text-sm font-medium text-ink-body">{children}</dd>
      </div>
      {note && <p className="mt-0.5 text-xs text-ink-muted">{note}</p>}
      <SourceRefs refs={refs} />
    </div>
  )
}

// ── M6: the AI "why" narration panel (kill-switched) ─────────────────────────
function NarrationSection({
  leafId,
  period,
  narration,
  enabled,
  canGenerate,
  canPublish,
}: {
  leafId: number
  period: Period
  narration: Narration | null
  enabled: boolean
  canGenerate: boolean
  canPublish: boolean
}) {
  const active = narration ? NARRATION_ACTIVE.includes(narration.status) : false

  // Poll while the background narration runs (a single on-demand narration needs
  // no ActionCable channel); stop as soon as it lands. useEffect ⇒ client-only.
  useEffect(() => {
    if (!active) return
    const timer = setInterval(
      () => router.reload({ only: ["narration", "can_publish", "can_generate"] }),
      3500,
    )
    return () => clearInterval(timer)
  }, [active])

  // Kill-switch off ⇒ clean no-op: the deterministic M4 dashboard is unaffected.
  if (!enabled) return null

  const base = `/admin/product-performance/${leafId}`
  const q = { year: period.year, month: period.month }
  const generate = () => router.post(`${base}/generate`, q, { preserveScroll: true })
  const publish = () => {
    if (window.confirm("Publikasikan narasi ini? Setelah terbit tidak bisa diubah.")) {
      router.patch(`${base}/publish`, q, { preserveScroll: true })
    }
  }

  const unknownTokens = narration?.validation?.unknown_tokens ?? []
  const tokens = narration ? narration.input_tokens + narration.output_tokens : 0

  return (
    <section className="mt-8 rounded-lg border border-hairline bg-surface p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-accent" aria-hidden="true" />
          <h2 className="mb-0 text-sm font-medium text-ink-display">Narasi “Kenapa” (AI)</h2>
          {narration && <NarrationStatusBadge status={narration.status} />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {narration?.md_body && (
            <a
              href={`${base}/export?year=${period.year}&month=${period.month}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm text-ink-body hover:text-accent"
            >
              <Download className="h-4 w-4" />
              .md
            </a>
          )}
          {canGenerate && !active && (
            <Button variant="secondary" size="sm" className="gap-1.5" onClick={generate}>
              <RefreshCw className="h-4 w-4" />
              {narration ? "Generate ulang" : "Generate"}
            </Button>
          )}
          {canPublish && !active && (
            <Button variant="primary" size="sm" className="gap-1.5" onClick={publish}>
              <Send className="h-4 w-4" />
              Publikasikan
            </Button>
          )}
        </div>
      </div>

      <p className="mt-1 text-sm text-ink-muted">
        Penjelasan naratif dari snapshot deterministik — hanya angka yang ada di data (anti-halusinasi, pencarian web nonaktif).
      </p>

      {active ? (
        <p className="mt-4 text-sm text-ink-muted">Narasi sedang diproses — halaman diperbarui otomatis…</p>
      ) : narration?.status === "failed" ? (
        <Alert tone="danger" title="Narasi gagal" className="mt-4">
          {narration.error_message ?? "Terjadi kesalahan saat membuat narasi."}
        </Alert>
      ) : narration?.md_body ? (
        <>
          {narration.status === "needs_review" && (
            <Alert tone="warning" title="Perlu ditinjau" className="mt-4">
              Validator tidak bisa mencocokkan {unknownTokens.length} angka ke context pack
              {unknownTokens.length > 0 && (
                <>
                  {" "}
                  (<span className="font-medium">{unknownTokens.join(", ")}</span>)
                </>
              )}
              . Angka yang ditandai kuning perlu dikoreksi sebelum dipublikasikan.
            </Alert>
          )}
          <div className="mt-4">
            <NarratedProse text={narration.md_body} unknownTokens={unknownTokens} />
          </div>
          {narration.sources.length > 0 && (
            <p className="mt-3 text-xs text-ink-muted">Sumber: {narrationSourceLabels(narration.sources).join(" · ")}</p>
          )}
          {narration.generated_at && (
            <p className="mt-2 text-xs text-ink-muted">
              {narration.model && <>Model {narration.model} · </>}
              {tokens > 0 && <>{tokens.toLocaleString("id-ID")} token · ${narration.cost.toFixed(4)} · </>}
              Dibuat {fmtDateTime(narration.generated_at)}
              {narration.published_at && <> · Terbit {fmtDateTime(narration.published_at)}</>}
            </p>
          )}
        </>
      ) : (
        <p className="mt-4 text-sm text-ink-muted">
          Belum ada narasi untuk periode ini.{canGenerate ? " Klik “Generate” untuk membuatnya." : ""}
        </p>
      )}
    </section>
  )
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
